/**
 * Collection slider (markup: snippets/collection-slider.liquid, style: assets/collection-slider.css).
 * Phones: native swipe. Desktop: arrows, mouse drag and a thin progress line.
 * The tile of the collection being viewed is centred when the page loads.
 */
class CollectionSlider extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.list = this.querySelector('.coll-slider__list');
    this.prev = this.querySelector('.coll-slider__arrow--prev');
    this.next = this.querySelector('.coll-slider__arrow--next');
    this.bar = this.querySelector('.coll-slider__bar');
    if (!this.list) return;

    this.list.addEventListener('scroll', () => this.update(), { passive: true });
    window.addEventListener('resize', () => this.update(), { passive: true });
    this.prev?.addEventListener('click', () => this.scrollByPage(-1));
    this.next?.addEventListener('click', () => this.scrollByPage(1));
    this.enableDrag();

    // Centre the current collection's tile (only the slider scrolls, never the page).
    const current = this.list.querySelector('.is-current');
    if (current) {
      const item = current.closest('.coll-slider__item');
      const target = item.offsetLeft - (this.list.clientWidth - item.offsetWidth) / 2;
      this.list.scrollLeft = Math.max(0, target);
    }
    this.update();
  }

  scrollByPage(direction) {
    this.list.scrollBy({ left: direction * this.list.clientWidth * 0.8, behavior: 'smooth' });
  }

  update() {
    const max = this.list.scrollWidth - this.list.clientWidth;
    const fits = max <= 2;
    this.classList.toggle('is-static', fits);
    if (this.prev) this.prev.hidden = fits || this.list.scrollLeft <= 2;
    if (this.next) this.next.hidden = fits || this.list.scrollLeft >= max - 2;
    if (this.bar && !fits) {
      const width = Math.max(10, (this.list.clientWidth / this.list.scrollWidth) * 100);
      this.bar.style.width = `${width}%`;
      this.bar.style.left = `${(this.list.scrollLeft / max) * (100 - width)}%`;
    }
  }

  // Mouse drag (touch screens already scroll natively). A drag never counts as a click on a tile.
  enableDrag() {
    let startX = 0;
    let startLeft = 0;
    let dragging = false;
    let moved = false;

    this.list.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startLeft = this.list.scrollLeft;
    });

    window.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) > 5) {
        moved = true;
        this.list.classList.add('is-dragging');
      }
      if (moved) this.list.scrollLeft = startLeft - delta;
    });

    window.addEventListener('pointerup', () => {
      if (!dragging) return;
      dragging = false;
      this.list.classList.remove('is-dragging');
    });

    this.list.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      },
      true,
    );
  }
}

if (!customElements.get('collection-slider')) customElements.define('collection-slider', CollectionSlider);
